import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router';
import { interval, map, takeWhile, tap } from 'rxjs';

import { GoBack } from '@/component';
import { useUpdateExpireTimeMutation } from '@/hook';
import {
  ProgressActivity,
  VFlex,
  VLoadingSpinner,
  useSubscriptions,
} from '@/package';
import { usePcdRenderingStore, useTaskFrameStore } from '@/store';

import TaskLayoutHeaderCenter from './TaskLayoutHeaderCenter';
import TaskLayoutHeaderOperation from './TaskLayoutHeaderOperation';
import { TaskLayoutHeaderReportButton } from './TaskLayoutHeaderReportButton';
import { TrackIdSessionTimer } from './TrackIdSessionTimer';

const TaskLayoutHeader = () => {
  const { t } = useTranslation();
  const { getCurrencPcdCount } = usePcdRenderingStore();
  const pcdPointLength = getCurrencPcdCount();
  const [
    allFrameList,
    currentFrame,
    selectedTask,
    isFrameDataLoading,
    isFrameFileLoading,
  ] = useTaskFrameStore(state => [
    state.allFrameList,
    state.currentFrame,
    state.selectedTask,
    state.isFrameDataLoading,
    state.isFrameFileLoading,
  ]);
  const location = useLocation();
  const { mutateAsync: updateExpireTime } = useUpdateExpireTimeMutation();
  const [remainingTime, setRemainingTime] = useState(305);
  const trackSession =
    currentFrame?.usableTrackSession ?? currentFrame?.curTrackSession;
  const goBackPath = location.search.match(/myWork=true/)
    ? `/my-work?selectedTaskId=${selectedTask?.id}`
    : `/project/${selectedTask?.projectID}?selectedTaskId=${selectedTask?.id}`;

  useSubscriptions(() => {
    if (!trackSession) {
      setRemainingTime(0);

      return [];
    }

    const expiredAt = new Date(trackSession.expiredAt).getTime();
    const getRemainingTime = () =>
      Math.max(Math.floor((expiredAt - Date.now()) / 1000), 0);

    setRemainingTime(getRemainingTime());

    return [
      interval(1000)
        .pipe(
          map(getRemainingTime),
          tap(remainingTime => setRemainingTime(remainingTime)),
          takeWhile(remainingTime => remainingTime > 0, true),
        )
        .subscribe(),
    ];
  }, [trackSession]);

  return (
    <VFlex className="h-11.5 w-screen px-4" items="center">
      <VFlex className="flex-1 overflow-hidden" gap={0.5} items="center">
        <GoBack to={goBackPath} />
        <div className="text-body-xl font-semibold">
          {selectedTask?.taskName ?? selectedTask?.orgPath}
        </div>
        <div className="h-4 w-px bg-gray-300" />
        <VFlex
          className="flex-1 overflow-hidden text-body-lg text-gray-500"
          items="center"
          gap={0.125}
        >
          <div className="w-fit max-w-full overflow-hidden text-ellipsis">
            {useTaskFrameStore.getState().isValidFrameDetail() &&
              allFrameList[currentFrame!.frameSeq - 1]?.filename}
          </div>

          <div className="text-body-md">
            ({Intl.NumberFormat('en').format(pcdPointLength)}points)
          </div>
        </VFlex>
      </VFlex>

      <VFlex className="flex-1" justify="center" items="center">
        <VFlex className="w-fit" justify="center" items="center">
          {isFrameFileLoading ? (
            <VFlex
              className="h-[30px] w-[320px] rounded-sm bg-gray-200 text-body-sm text-status-inProgress"
              gap={0.125}
              items="center"
              justify="center"
            >
              <VLoadingSpinner className="size-4! text-status-inProgress!" />
              <span>{t('common.loading.cancerophobia')}</span>
            </VFlex>
          ) : (
            useTaskFrameStore.getState().isValidFrameDetail() && (
              <TaskLayoutHeaderCenter />
            )
          )}
        </VFlex>
      </VFlex>

      <VFlex className="flex-1" items="center" justify="end" gap={0.25}>
        {isFrameDataLoading && (
          <ProgressActivity className="animate-spin text-status-inProgress" />
        )}

        {selectedTask && currentFrame && trackSession && remainingTime > 0 && (
          <TrackIdSessionTimer
            remainingTime={remainingTime}
            onExtend={() => {
              return updateExpireTime({
                frameSeq: currentFrame.frameSeq,
                taskID: selectedTask.id,
                trackSessionID: trackSession.id,
              });
            }}
          />
        )}

        <TaskLayoutHeaderReportButton />

        <TaskLayoutHeaderOperation />
      </VFlex>
    </VFlex>
  );
};

export default TaskLayoutHeader;
